// Re-usable circular avatar for an admin / agent row.
//
// Shows the uploaded photo when present; otherwise renders the
// initial of `username` in a coloured circle. Size + ring colour
// configurable so the same component renders in the top-bar (32px),
// the profile page hero (96px), and the agent list (40px).

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZES: Record<Size, { box: string; text: string }> = {
  xs: { box: 'w-6 h-6',   text: 'text-[10px]' },
  sm: { box: 'w-8 h-8',   text: 'text-xs' },
  md: { box: 'w-10 h-10', text: 'text-sm' },
  lg: { box: 'w-16 h-16', text: 'text-xl' },
  xl: { box: 'w-24 h-24', text: 'text-2xl' },
};

// Deterministic colour pick from username — so the same agent always
// gets the same fallback colour even before they upload a photo.
const PALETTE = [
  'bg-gradient-to-br from-emerald-500 to-cyan-700',
  'bg-gradient-to-br from-fuchsia-500 to-purple-700',
  'bg-gradient-to-br from-amber-500 to-orange-700',
  'bg-gradient-to-br from-sky-500 to-indigo-700',
  'bg-gradient-to-br from-rose-500 to-red-700',
  'bg-gradient-to-br from-teal-500 to-cyan-700',
  'bg-gradient-to-br from-violet-500 to-fuchsia-700',
  'bg-gradient-to-br from-lime-500 to-emerald-700',
];

function pickColour(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

type Props = {
  url?: string | null;
  username?: string;
  size?: Size;
  ring?: boolean;
  className?: string;
};

export default function AdminAvatar({ url, username = '?', size = 'md', ring = false, className = '' }: Props) {
  const { box, text } = SIZES[size];
  const initial = (username || '?').trim().charAt(0).toUpperCase();
  const ringCls = ring ? 'ring-2 ring-emerald-400/60 ring-offset-2 ring-offset-neutral-950' : '';
  if (url) {
    return (
      <img
        src={url}
        alt={username}
        className={`${box} rounded-full object-cover ${ringCls} ${className}`}
      />
    );
  }
  return (
    <div
      className={`${box} ${pickColour(username)} ${ringCls}
                  rounded-full grid place-items-center font-bold text-white shadow-md ${className}`}
      title={username}
    >
      <span className={text}>{initial}</span>
    </div>
  );
}
